// src/utils/safeSearchClient.js
import { algoliasearch } from "algoliasearch";

// Build an empty result payload matching Algolia's multi-search shape
function emptyResultsFor(requests = []) {
  return {
    results: (requests || []).map(() => ({
      hits: [],
      nbHits: 0,
      nbPages: 0,
      page: 0,
      processingTimeMS: 0,
      hitsPerPage: 0,
      exhaustiveNbHits: false,
      params: "",
    })),
  };
}

export function createSafeSearchClient(appId, apiKey, { timeoutMs = 6000 } = {}) {
  // If env missing, return a noop client that never throws
  if (!appId || !apiKey) {
    return {
      search: async (requests) => emptyResultsFor(requests),
      searchForFacetValues: async () => [],
    };
  }

  // Silence transporter error logs to avoid noisy console spam on timeouts
  const quietLogger = { debug() {}, info() {}, error() {} };
  const base = algoliasearch(appId, apiKey, { logger: quietLogger });

  // Wrap search to provide timeout and error isolation
  const safeSearch = async (requests) => {
    try {
      const p = base.search(requests);
      // Avoid unhandled rejection when racing with timeout
      p.catch(() => {});
      if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return await p;
      const t = new Promise((_, rej) => setTimeout(() => rej(new Error("Timeout")), timeoutMs));
      return await Promise.race([p, t]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[Algolia] search error handled:", e?.message || e);
      return emptyResultsFor(requests);
    }
  };

  // Proxy other methods as-is when available; guard to no-op on failure
  return {
    ...base,
    search: safeSearch,
    searchForFacetValues: async (...args) => {
      try { return await base.searchForFacetValues?.(...args); }
      catch (e) { console.error("[Algolia] sffv error handled:", e?.message || e); return []; }
    },
  };
}
